import { useQuery } from '@tanstack/react-query';
import { get } from '../../../api/client';
import type { OrderItem, PaginationMeta } from '../../../types';
import { overPaymentOf, pendingBalanceOf } from '../../../utils/paymentAmount';

export interface Transaction {
  id: string;
  orderNumber: string | null;
  orderCategory: string | null;
  orderStatus: string | null;
  reviewStatus: string | null;
  paymentStatus: string | null;
  paymentMethod: string | null;
  paymentDate: string | null;
  orderAmount: number;
  paidAmount: number;
  unpaidAmount: number;
  orderStartDate: string | null;
  createdAt: string | null;
  customerName: string | null;
  serviceTypeName: string | null;
}

export interface MonthlyStatement {
  key: string;
  month: string;
  income: number;
  expense: number;
  profit: number;
  orders: number;
  paidAmount: number;
  pendingAmount: number;
  /** 多收（实收超过应收的部分）：与 pendingAmount 并列，不互抵 */
  overPaymentAmount: number;
}

export interface AccountingOverviewData {
  totalIncome: number;
  totalExpense: number;
  netProfit: number;
  totalPaid: number;
  totalPending: number;
  /** 多收合计（预收余额）：不是负债，也不冲抵 totalPending */
  totalOverPayment: number;
  thisMonthIncome: number;
  thisMonthExpense: number;
  thisMonthProfit: number;
  paymentMethods: { name: string; value: number; amount: number }[];
}

export interface AccountingData {
  overview: AccountingOverviewData;
  monthly: MonthlyStatement[];
  transactions: Transaction[];
}

function getLast12Months() {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: `${d.getFullYear()}年${d.getMonth() + 1}月`,
    };
  });
}

type OrderRow = {
  id: string;
  orderNumber: string | null;
  orderCategory: string | null;
  orderStatus: string | null;
  reviewStatus: string | null;
  paymentStatus: string | null;
  paymentMethod: string | null;
  paymentDate: string | null;
  orderAmount: number | null;
  paidAmount: number | null;
  orderStartDate: string | null;
  createdAt: string | null;
  customer: { fullName: string | null } | null;
  serviceType: { name: string | null } | null;
};

/**
 * 待收款：逐单夹零 Σ max(应收 − 实收, 0)。
 * 实收允许大于订单金额（预付/挂账），多收的那张单待收记 0——
 * 用「Σ应收 − Σ实收」会让它的负余款去冲抵其他单的欠款，整月/整站待收被报少。
 * 口径与后端 orders.service.getOrderStats、管理端看板 pendingAmount 同源。
 */
function sumPending(rows: OrderRow[]): number {
  return rows.reduce((s, r) => s + pendingBalanceOf(Number(r.orderAmount) || 0, Number(r.paidAmount) || 0), 0);
}

/**
 * 多收：Σ max(实收 − 应收, 0)，即预收余额。
 * 与 sumPending 并列而不是互补——两个数相加不等于任何一方的净额，界面上必须分列。
 */
function sumOverPayment(rows: OrderRow[]): number {
  return rows.reduce((s, r) => s + overPaymentOf(Number(r.orderAmount) || 0, Number(r.paidAmount) || 0), 0);
}

export function useAccountingData(dateFrom?: string, dateTo?: string) {
  // 使用 useQuery 获取会计数据
  const { data, isLoading: loading, error, refetch: refresh } = useQuery({
    queryKey: ['accountingData', dateFrom, dateTo],
    queryFn: async () => {
      // 注意：不向后端传 dateFrom/dateTo。
      // 后端基于 order_start_date 做严格 gte/lte 过滤，会误伤未设置 order_start_date 的订单；
      // 统一改由前端按 orderStartDate || createdAt 做日期窗口过滤，并保留未设日期的订单。
      const params: Record<string, unknown> = {
        orderBy: 'orderStartDate',
        orderDirection: 'desc',
        pageSize: 9999, // 一次拉全量订单用于前端聚合
      };

      const response = await get<{ data: OrderItem[]; pagination: PaginationMeta }>('/orders', { params });
      // 兼容极端情况：response 可能是纯数组或 { data }
      const rawList: OrderItem[] = Array.isArray(response)
        ? (response as OrderItem[])
        : Array.isArray((response as { data?: OrderItem[] })?.data)
        ? ((response as { data: OrderItem[] }).data)
        : [];

      const rows = rawList.map(order => ({
        ...order,
        customer: order.customer || null,
        serviceType: order.serviceType || null
      })) as OrderRow[];

      const now = new Date();
      const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      // 前端日期窗口过滤（仅作用于 overview 与 transactions；monthly 固定为最近 12 个月）
      const fromT = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
      const toT = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : Infinity;
      const inWindow = (r: OrderRow) => {
        const d = r.orderStartDate || r.createdAt;
        if (!d) return true; // 未设置日期的订单默认保留，避免漏统计
        const t = new Date(d).getTime();
        if (Number.isNaN(t)) return true;
        return t >= fromT && t <= toT;
      };

      // 分类器：
      //   isExpense     = 支出视角（当前用户为支出方）
      //   isRejected    = 已拒绝，彻底不计入任何统计
      //   isCancelled   = 已取消，订单金额不计，但 paid_amount 作为违约金计入已收/支
      const isExpense = (r: OrderRow) => (r.orderCategory ?? '').includes('支出');
      const isRejected = (r: OrderRow) => r.reviewStatus === '已拒绝';
      const isCancelled = (r: OrderRow) => r.orderStatus === '已取消';

      // overview 作用域：先按日期窗口过滤
      const windowRows = rows.filter(inWindow);
      // 活跃行（非已取消且非已拒绝）
      const activeRows = windowRows.filter(r => !isRejected(r) && !isCancelled(r));
      // 违约金行（已取消但非已拒绝，paid_amount>0）
      const penaltyRows = windowRows.filter(r => !isRejected(r) && isCancelled(r) && (Number(r.paidAmount) || 0) > 0);

      const incomeActive = activeRows.filter(r => !isExpense(r));
      const expenseActive = activeRows.filter(r => isExpense(r));
      const incomePenalty = penaltyRows.filter(r => !isExpense(r));
      const expensePenalty = penaltyRows.filter(r => isExpense(r));

      const totalIncome =
        incomeActive.reduce((s, r) => s + (Number(r.orderAmount) || 0), 0) +
        incomePenalty.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0); // 活跃收入 + 已取消收入订单的违约金
      const totalExpense =
        expenseActive.reduce((s, r) => s + (Number(r.orderAmount) || 0), 0) +
        expensePenalty.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0); // 活跃支出 + 已取消支出订单的违约金
      const activePaid = incomeActive.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0);
      const penaltyPaid = incomePenalty.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0);
      const totalPaid = activePaid + penaltyPaid; // 含已取消违约金
      // 待收款只对活跃收入订单计算（违约金已全额到账，不存在“待收”）
      const totalPending = sumPending(incomeActive);
      // 多收同样只对活跃收入订单算：它是客户预付下来的余额，不是违约金也不是负债
      const totalOverPayment = sumOverPayment(incomeActive);

      // 支付方式分布：仅统计实际到账金额（paidAmount），包含活跃收入行 + 已取消违约金
      const pmMap: Record<string, { value: number; amount: number }> = {};
      [...incomeActive, ...incomePenalty].forEach(r => {
        const paid = Number(r.paidAmount) || 0;
        if (paid <= 0) return;
        const pm = r.paymentMethod || '未记录';
        if (!pmMap[pm]) pmMap[pm] = { value: 0, amount: 0 };
        pmMap[pm].value += 1;
        pmMap[pm].amount += paid;
      });
      const paymentMethods = Object.entries(pmMap)
        .map(([name, d]) => ({ name, value: d.value, amount: d.amount }))
        .sort((a, b) => b.amount - a.amount);

      // 月度报表：固定展示最近 12 个月（不受日期筛选影响），基于全量 rows
      const monthRowsAll = rows.filter(r => !isRejected(r));
      const monthActiveAll = monthRowsAll.filter(r => !isCancelled(r));
      const monthPenaltyAll = monthRowsAll.filter(r => isCancelled(r) && (Number(r.paidAmount) || 0) > 0);
      const mIncomeActiveAll = monthActiveAll.filter(r => !isExpense(r));
      const mExpenseActiveAll = monthActiveAll.filter(r => isExpense(r));
      const mIncomePenaltyAll = monthPenaltyAll.filter(r => !isExpense(r));
      const mExpensePenaltyAll = monthPenaltyAll.filter(r => isExpense(r));

      const months = getLast12Months();
      const monthKey = (r: OrderRow) => (r.orderStartDate || r.createdAt || '').slice(0, 7);
      const monthly: MonthlyStatement[] = months.map(({ key, label }) => {
        const mActiveIncome = mIncomeActiveAll.filter(r => monthKey(r) === key);
        const mActiveExpense = mExpenseActiveAll.filter(r => monthKey(r) === key);
        const mIncomePenalty = mIncomePenaltyAll.filter(r => monthKey(r) === key);
        const mExpensePenalty = mExpensePenaltyAll.filter(r => monthKey(r) === key);

        const mIncomeAmt = mActiveIncome.reduce((s, r) => s + (Number(r.orderAmount) || 0), 0);
        const mExpenseAmt =
          mActiveExpense.reduce((s, r) => s + (Number(r.orderAmount) || 0), 0) +
          mExpensePenalty.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0);
        const mActivePaid = mActiveIncome.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0);
        const mPenaltyPaid = mIncomePenalty.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0);
        const mPaid = mActivePaid + mPenaltyPaid;

        return {
          key,
          month: label,
          income: mIncomeAmt,
          expense: mExpenseAmt,
          profit: mIncomeAmt - mExpenseAmt,
          orders: mActiveIncome.length + mActiveExpense.length + mIncomePenalty.length + mExpensePenalty.length,
          paidAmount: mPaid,
          pendingAmount: sumPending(mActiveIncome),
          overPaymentAmount: sumOverPayment(mActiveIncome),
        };
      }).reverse(); // latest first

      const thisMonth = monthly.find(m => m.key === thisMonthKey);

      // 流水明细：展示全部非已拒绝订单（不受日期窗口限制）
      // 预约系统中新建订单的 order_start_date 通常在未来，若受日期窗口限制会导致新订单不显示
      const txRows = rows.filter(r => !isRejected(r));
      const transactions: Transaction[] = txRows.map(r => ({
        id: r.id,
        orderNumber: r.orderNumber,
        orderCategory: r.orderCategory,
        orderStatus: r.orderStatus,
        reviewStatus: r.reviewStatus,
        paymentStatus: r.paymentStatus,
        paymentMethod: r.paymentMethod,
        paymentDate: r.paymentDate,
        orderAmount: Number(r.orderAmount) || 0,
        paidAmount: Number(r.paidAmount) || 0,
        unpaidAmount: !isExpense(r) && !isCancelled(r)
          ? pendingBalanceOf(Number(r.orderAmount) || 0, Number(r.paidAmount) || 0)
          : 0,
        orderStartDate: r.orderStartDate,
        createdAt: r.createdAt,
        customerName: r.customer?.fullName || null,
        serviceTypeName: r.serviceType?.name || null,
      }));

      return {
        overview: {
          totalIncome,
          totalExpense,
          netProfit: totalIncome - totalExpense,
          totalPaid,
          totalPending,
          totalOverPayment,
          thisMonthIncome: thisMonth?.income || 0,
          thisMonthExpense: thisMonth?.expense || 0,
          thisMonthProfit: thisMonth?.profit || 0,
          paymentMethods,
        },
        monthly,
        transactions,
      };
    },
  });

  return { data, loading, error: error?.message || null, refresh };
}
